import { useEffect } from "react";
import { BUILD_VERSION } from "config/buildVersion";
import criticalOperationStore from "stores/criticalOperationStore";

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const RELOAD_GUARD_KEY = "travelbay_reload_for_version";

const extractVersion = (html) => {
  const match = html.match(
    /<meta\s+name="travelbay-build-version"\s+content="([^"]*)"/i,
  );
  return match ? match[1] : null;
};

/**
 * Polls the served index.html (bypassing HTTP cache) for its build-version
 * meta tag. If it differs from the version this tab was built with, the
 * deploy has moved on — reload once to pick up the new bundle. Skips the
 * reload entirely while a payment/booking/other critical write is in
 * flight, and never reloads more than once per version so a flaky check
 * can't loop.
 */
const checkForNewVersion = async () => {
  if (criticalOperationStore.isInProgress) return;

  let html;
  try {
    const response = await fetch(`${process.env.PUBLIC_URL}/index.html`, {
      cache: "no-store",
    });
    html = await response.text();
  } catch {
    return; // offline or transient network error — try again next interval
  }

  const remoteVersion = extractVersion(html);
  if (!remoteVersion || remoteVersion === BUILD_VERSION) return;

  let alreadyReloadedForThisVersion = false;
  try {
    alreadyReloadedForThisVersion =
      window.sessionStorage.getItem(RELOAD_GUARD_KEY) === remoteVersion;
  } catch {
    // if sessionStorage is unavailable, fall through and reload once anyway
  }
  if (alreadyReloadedForThisVersion) return;

  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, remoteVersion);
  } catch {
    // ignore — worst case we reload again next interval
  }

  window.location.reload();
};

const VersionWatcher = () => {
  useEffect(() => {
    const id = setInterval(checkForNewVersion, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return null;
};

export default VersionWatcher;
